import {
  ASK_PUNTT_CAMPAIGN_PATH,
  ASK_PUNTT_CHAT_PATH,
  ASK_PUNTT_PROJECT_PATH,
  buildChatPath,
  buildChatReviewPath,
  buildProjectChatPath,
  buildProjectFeedPath,
  buildProjectReviewPath,
} from "../askPunttPaths";
import {
  type CampaignSearch,
  type CampaignSearchInput,
  campaignSearch,
} from "../campaignSearch";
import {
  type ChatSearch,
  type ChatSearchInput,
  chatSearch,
} from "../chatSearch";

export {
  ASK_PUNTT_CAMPAIGN_PATH,
  ASK_PUNTT_CHAT_PATH,
  buildChatPath,
  buildChatReviewPath,
  buildProjectChatPath,
  buildProjectFeedPath,
  buildProjectReviewPath,
};

export type AskPunttChatNavSource = {
  _id: string;
  projectId?: string | null;
  projectChatKind?: "shared" | "personal" | null;
};

export function isProjectPersonalChat(chat: AskPunttChatNavSource): boolean {
  return Boolean(chat.projectId && chat.projectChatKind === "personal");
}

export function askPunttProjectLink(
  projectId: string,
  search?: CampaignSearchInput,
) {
  if (search?.review) {
    return buildProjectReviewPath(
      projectId,
      search.review,
      undefined,
      search.version,
    );
  }
  if (search?.chatId) {
    return buildProjectChatPath(projectId, search.chatId);
  }
  return buildProjectFeedPath(projectId, search);
}

export function askPunttChatLink(chatId: string, search?: ChatSearchInput) {
  if (search?.review) {
    return buildChatReviewPath(
      chatId,
      search.review,
      undefined,
      search.version,
    );
  }
  return buildChatPath(chatId, search);
}

export function askPunttReviewLink(
  scope:
    | { kind: "project"; projectId: string; search?: CampaignSearchInput }
    | { kind: "chat"; chatId: string; search?: ChatSearchInput },
) {
  if (scope.kind === "project") {
    return askPunttProjectLink(scope.projectId, scope.search);
  }
  return askPunttChatLink(scope.chatId, scope.search);
}

export function askPunttChatNavLink(chat: AskPunttChatNavSource) {
  if (isProjectPersonalChat(chat)) {
    return buildProjectChatPath(chat.projectId!, chat._id);
  }
  return buildChatPath(chat._id);
}

/**
 * A router match as consumed by the sidebar active-state checks. We read the
 * route PARAMS (`projectId` / `chatId`) rather than the `pathname` string,
 * because the deployed epic env serves carrie under a basepath (e.g.
 * `/ap-sidebar-hierarchy`) — so `match.pathname` is NOT the bare
 * `/ask-puntt/projects/<id>` and a hardcoded string compare silently never
 * matches. Params are basepath- and trailing-slash-immune.
 */
export type AskPunttRouteMatch = {
  pathname?: string;
  params?: Record<string, unknown>;
};

export function isAskPunttChatNavActive(
  chat: AskPunttChatNavSource,
  matches: AskPunttRouteMatch[],
  locationSearch?: Record<string, unknown>,
): boolean {
  if (isProjectPersonalChat(chat)) {
    const onProject = matches.some(
      (m) => m.params?.projectId === chat.projectId,
    );
    if (!onProject) return false;
    const paramChatId = matches.find((m) => m.params?.chatId)?.params?.chatId;
    if (typeof paramChatId === "string") {
      return paramChatId === chat._id;
    }
    const searchChatId =
      typeof locationSearch?.chatId === "string"
        ? locationSearch.chatId
        : undefined;
    return searchChatId === chat._id;
  }
  return matches.some((m) => m.params?.chatId === chat._id);
}

export function isViewingAskPunttChat(
  chat: AskPunttChatNavSource,
  pathname: string,
  searchParams: URLSearchParams,
): boolean {
  if (isProjectPersonalChat(chat)) {
    return (
      (pathname.includes(`/ask-puntt/projects/${chat.projectId}/chats/`) &&
        pathname.endsWith(`/${chat._id}`)) ||
      (pathname.endsWith(`/ask-puntt/projects/${chat.projectId}`) &&
        searchParams.get("chatId") === chat._id)
    );
  }
  return pathname.includes(`/ask-puntt/${chat._id}`);
}

export type { CampaignSearch, ChatSearch };
